import { makeAutoObservable, runInAction } from 'mobx'
import { counselorSessionStore } from './counselorSessionStore'
import { fetchPlatformOrganizations } from 'api/admin'

const STORAGE_KEY = 'kandor.um.organizationContextId'

class OrganizationContextStore {
	selectedOrganizationId = null
	selectedOrganizationName = null
	organizations = []
	loadStatus = 'idle'
	error = null
	_inflight = null

	constructor() {
		makeAutoObservable(this, { _inflight: false }, { autoBind: true })
		const saved = localStorage.getItem(STORAGE_KEY)
		if (saved) {
			const parsed = Number(saved)
			this.selectedOrganizationId = Number.isFinite(parsed) && parsed > 0 ? parsed : null
		}
	}

	get isPlatformCeo() {
		return counselorSessionStore.isPlatformCeo
	}

	get isOrganizationManager() {
		return counselorSessionStore.isOrganizationManager
	}

	get isReady() {
		if (this.isPlatformCeo) {
			return this.selectedOrganizationId != null
		}
		return this.selectedOrganizationId != null
	}

	get contextHeaders() {
		if (!this.selectedOrganizationId) {
			return {}
		}
		return {
			'X-Organization-Context': String(this.selectedOrganizationId),
		}
	}

	async ensureInitialized({ force = false } = {}) {
		await counselorSessionStore.ensureLoaded()

		if (counselorSessionStore.isOrganizationManager) {
			const org = counselorSessionStore.profile?.organization
			runInAction(() => {
				this.selectedOrganizationId = org?.id ?? null
				this.selectedOrganizationName = org?.name ?? null
			})
			return
		}

		if (counselorSessionStore.isPlatformCeo) {
			await this.loadOrganizations({ force })
			if (this.selectedOrganizationId) {
				const match = this.organizations.find(
					org => Number(org.id) === Number(this.selectedOrganizationId),
				)
				if (match) {
					runInAction(() => {
						this.selectedOrganizationName = match.name
					})
				} else {
					runInAction(() => {
						this.selectedOrganizationId = null
						this.selectedOrganizationName = null
					})
					localStorage.removeItem(STORAGE_KEY)
				}
			}
		}
	}

	async loadOrganizations({ force = false } = {}) {
		if (!force && this.loadStatus === 'success' && this.organizations.length) {
			return this.organizations
		}
		if (this._inflight) return this._inflight

		this.loadStatus = 'loading'
		this.error = null

		this._inflight = (async () => {
			try {
				const payload = await fetchPlatformOrganizations()
				const rows = (payload?.organizations || []).map(row => ({
					id: row.id,
					name: row.name || `Organization ${row.id}`,
				}))
				runInAction(() => {
					this.organizations = rows
					this.loadStatus = 'success'
				})
				return rows
			} catch (error) {
				runInAction(() => {
					this.organizations = []
					this.loadStatus = 'error'
					this.error = error?.message || 'Failed to load organizations'
				})
				throw error
			} finally {
				this._inflight = null
			}
		})()

		return this._inflight
	}

	setSelectedOrganization(organizationId, organizationName) {
		const id = Number(organizationId)
		if (!Number.isFinite(id) || id <= 0) return
		this.selectedOrganizationId = id
		this.selectedOrganizationName = organizationName || null
		localStorage.setItem(STORAGE_KEY, String(id))
	}

	clearSelectedOrganization() {
		this.selectedOrganizationId = null
		this.selectedOrganizationName = null
		localStorage.removeItem(STORAGE_KEY)
	}
}

export const organizationContextStore = new OrganizationContextStore()

export const ORGANIZATION_CONTEXT_HEADER = 'X-Organization-Context'
